import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { DadosVariavelService } from 'src/services/dados-variavel.service';
import { EstacaoService } from 'src/services/estacao.service';
import { TipoVariavelService } from 'src/services/tipo-variavel.service';
import { PermissoesService } from 'src/services/permissoes.service';
import { OnixsatService } from 'src/services/onixsat.service';
import { firstValueFrom } from 'rxjs';
import * as moment from 'moment';

@Component({
    selector: 'app-dados-variavel',
    templateUrl: './dados-variavel.component.html',
    styleUrls: ['./dados-variavel.component.scss'],
    standalone: false
})
export class DadosVariavelComponent implements OnInit, AfterViewInit {
  data: any[] = [];
  loading: boolean = false;
  loadingIntegracao: boolean = false;
  displayModal: boolean = false;
  isEditMode: boolean = false;
  form: FormGroup;
  
  // Filtros
  estacaoSelecionada: number | null = null;
  tipoVariavelSelecionado: number | null = null;
  dataInicial: Date | null = null;
  dataFinal: Date | null = null;
  
  // Dropdowns
  dropDownEstacoes: any[] = [];
  dropDownTipoVariavel: any[] = [];
  
  // Permissões
  direitoInserir: boolean = false;
  direitoEditar: boolean = false;
  direitoExcluir: boolean = false;
  
  // Flag para controlar se já foi filtrado
  dadosFiltrados: boolean = false;
  loadingReprocessamento: boolean = false;
  @ViewChild('tableWrapper', { static: false }) tableWrapper: ElementRef;
  @ViewChild('fileUpload') fileUpload: FileUpload;
  scrollHeight: string = '600px';

  // Wizard de importação em lote
  mostrarWizardImportacao: boolean = false;
  passoWizard: number = 1; // 1: upload, 2: mapeamento, 3: preview, 4: importar
  arquivoSelecionado: File | null = null;
  estacaoWizard: number | null = null;
  processandoArquivo: boolean = false;
  dadosProcessados: any[] = [];
  previewData: any[] = [];
  previewLinhas: any[] = [];
  somenteNovos = false;
  previewVariaveis: number[] = [];
  colunasArquivo: string[] = [];
  colunasValor: string[] = [];
  colunaDataHora: string | null = null;
  excelUsa1904 = false;
  mapeamentoColunas: { [key: string]: number | null } = {};
  dadosExistentes: Map<string, { id: number; valor: number }[]> = new Map();

  constructor(
    private dadosVariavelService: DadosVariavelService,
    private estacaoService: EstacaoService,
    private tipoVariavelService: TipoVariavelService,
    private onixsatService: OnixsatService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    protected permissaoService: PermissoesService,
  ) {
    this.form = this.formBuilder.group({
      id: [null],
      tipo_variavel_id: [null, Validators.required],
      data_hora_dado: [null, Validators.required],
      valor: [null, [Validators.required, Validators.min(0)]]
    });
  }

  ngOnInit(): void {
    this.direitoInserir = this.permissaoService.can('dados_variavel_inserir');
    this.direitoEditar = this.permissaoService.can('dados_variavel_editar');
    this.direitoExcluir = this.permissaoService.can('dados_variavel_excluir');
    
    // Inicializar como false - só habilita após filtrar
    this.dadosFiltrados = false;
    
    this.carregarEstacoes();
    this.carregarTiposVariavel();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateScrollHeight();
    }, 200);
  }

  calculateScrollHeight(): void {
    if (this.tableWrapper && this.tableWrapper.nativeElement) {
      const wrapperHeight = this.tableWrapper.nativeElement.offsetHeight;
      const calculatedHeight = wrapperHeight - 120;
      this.scrollHeight = `${Math.max(calculatedHeight, 300)}px`;
    }
  }

  carregarEstacoes(): void {
    this.estacaoService.lookup().subscribe({
      next: (res) => {
        const estacoes = res || [];
        this.dropDownEstacoes = estacoes.map((item: any) => ({
          label: item.descricao || `Estação ${item.id}`,
          value: item.id
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar estações:', err);
      }
    });
  }

  carregarTiposVariavel(): void {
    this.tipoVariavelService.index().subscribe({
      next: (res) => {
        const tipos = res.data || res || [];
        this.dropDownTipoVariavel = tipos.map((item: any) => ({
          label: item.tipo_variavel || `Tipo ${item.id}`,
          value: item.id
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar tipos de variável:', err);
      }
    });
  }

  filtrar(): void {
    if (!this.estacaoSelecionada) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Selecione uma estação' 
      });
      return;
    }

    if (!this.dataInicial || !this.dataFinal) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Selecione o período (data inicial e final)' 
      });
      return;
    }

    this.loading = true;
    
    // Converte datas para horário de Brasília (UTC-3) como no dashboard
    const dataInicialBrasilia = moment(this.dataInicial).utcOffset(-180);
    const dataFinalBrasilia = moment(this.dataFinal).utcOffset(-180);
    
    const filtros: any = {
      estacao_id: this.estacaoSelecionada,
      data_inicial: dataInicialBrasilia.format('YYYY-MM-DD'),
      data_final: dataFinalBrasilia.format('YYYY-MM-DD'),
      order_by: 'data_hora_dado',
      order_dir: 'desc'
    };
    
    // Só adiciona tipo_variavel_id se houver seleção
    if (this.tipoVariavelSelecionado) {
      filtros.tipo_variavel_id = this.tipoVariavelSelecionado;
    }

    this.dadosVariavelService.listar(filtros).subscribe({
      next: (res) => {
        this.data = res.data || res || [];
        this.loading = false;
        this.dadosFiltrados = true; // Marca que os dados foram filtrados
        setTimeout(() => {
          this.calculateScrollHeight();
        }, 200);
        
        if (this.data.length === 0) {
          this.messageService.add({ 
            severity: 'info', 
            summary: 'Informação', 
            detail: 'Nenhum registro encontrado para os filtros selecionados' 
          });
        }
      },
      error: (err) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: 'Erro ao carregar dados' 
        });
        this.loading = false;
        // Mesmo em caso de erro, marca como filtrado para habilitar botões
        this.dadosFiltrados = true;
      }
    });
  }

  openNew(): void {
    this.isEditMode = false;
    this.form.reset();
    // Restaurar validadores para modo de inserção
    this.form.get('tipo_variavel_id')?.setValidators([Validators.required]);
    this.form.get('tipo_variavel_id')?.updateValueAndValidity();
    this.form.get('data_hora_dado')?.setValidators([Validators.required]);
    this.form.get('data_hora_dado')?.updateValueAndValidity();
    
    // Define a data padrão como a data inicial do filtro, ou data atual se não houver filtro
    let dataPadrao = new Date();
    if (this.dataInicial) {
      dataPadrao = new Date(this.dataInicial);
    }
    
    this.form.patchValue({
      tipo_variavel_id: this.tipoVariavelSelecionado,
      data_hora_dado: dataPadrao
    });
    this.displayModal = true;
  }

  edit(item: any): void {
    this.isEditMode = true;
    // Converte data/hora recebida da API para horário de Brasília (UTC-3) como no dashboard
    const dataHoraStr = item.data_hora_dado.replace('Z', '');
    const dataHoraBrasilia = moment(dataHoraStr).utcOffset(-180, true);
    
    this.form.patchValue({
      id: item.id,
      tipo_variavel_id: item.tipo_variavel_id,
      data_hora_dado: dataHoraBrasilia.toDate(),
      valor: item.valor
    });
    // No modo de edição, apenas o valor precisa ser validado
    if (this.isEditMode) {
      this.form.get('tipo_variavel_id')?.clearValidators();
      this.form.get('tipo_variavel_id')?.updateValueAndValidity();
      this.form.get('data_hora_dado')?.clearValidators();
      this.form.get('data_hora_dado')?.updateValueAndValidity();
    }
    this.displayModal = true;
  }

  save(): void {
    if (this.form.invalid) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Preencha todos os campos obrigatórios' 
      });
      return;
    }

    // Validação: para novos dados, verificar se a data está dentro do período do filtro
    if (!this.isEditMode) {
      if (!this.dataInicial || !this.dataFinal) {
        this.messageService.add({ 
          severity: 'warn', 
          summary: 'Atenção', 
          detail: 'É necessário filtrar por período antes de inserir um novo dado' 
        });
        return;
      }

      // Converte para horário de Brasília (UTC-3) como no dashboard
      const dataHoraDado = moment(this.form.value.data_hora_dado).utcOffset(-180);
      const dataInicialFiltro = moment(this.dataInicial).utcOffset(-180).startOf('day');
      const dataFinalFiltro = moment(this.dataFinal).utcOffset(-180).endOf('day');

      if (dataHoraDado.isBefore(dataInicialFiltro) || dataHoraDado.isAfter(dataFinalFiltro)) {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: `A data/hora do dado deve estar dentro do período do filtro (${moment(this.dataInicial).utcOffset(-180).format('DD/MM/YYYY')} a ${moment(this.dataFinal).utcOffset(-180).format('DD/MM/YYYY')})` 
        });
        return;
      }
    }

    this.loading = true;
    const formValue = this.form.value;

    if (this.isEditMode) {
      // Atualizar - apenas o valor pode ser editado
      const params = {
        valor: formValue.valor
      };

      this.dadosVariavelService.update(params, formValue.id).subscribe({
        next: () => {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Sucesso', 
            detail: 'Dado atualizado com sucesso' 
          });
          this.loading = false;
          this.displayModal = false;
          this.filtrar();
        },
        error: (err) => {
          const errorMsg = err?.error?.message || 'Erro ao atualizar dado';
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Erro', 
            detail: errorMsg 
          });
          this.loading = false;
        }
      });
    } else {
      // Inserir - todos os campos
      // Converte data/hora para horário de Brasília (UTC-3) como no dashboard
      const dataHoraBrasilia = moment(formValue.data_hora_dado).utcOffset(-180);
      const params = {
        estacao_id: this.estacaoSelecionada,
        tipo_variavel_id: formValue.tipo_variavel_id,
        data_hora_dado: dataHoraBrasilia.format('YYYY-MM-DD HH:mm:ss'),
        valor: formValue.valor
      };

      this.dadosVariavelService.store(params).subscribe({
        next: () => {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Sucesso', 
            detail: 'Dado criado com sucesso' 
          });
          this.loading = false;
          this.displayModal = false;
          this.filtrar();
        },
        error: (err) => {
          const errorMsg = err?.error?.message || 'Erro ao criar dado';
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Erro', 
            detail: errorMsg 
          });
          this.loading = false;
        }
      });
    }
  }

  delete(item: any): void {
    this.confirmationService.confirm({
      key: 'globalConfirm',
      message: 'Deseja realmente excluir este dado de variável?',
      header: 'Confirmação de Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.loading = true;
        this.dadosVariavelService.destroy(item.id).subscribe({
          next: () => {
            this.messageService.add({ 
              severity: 'success', 
              summary: 'Sucesso', 
              detail: 'Dado excluído com sucesso' 
            });
            this.filtrar();
          },
          error: (err) => {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Erro', 
              detail: 'Erro ao excluir dado' 
            });
            this.loading = false;
          }
        });
      }
    });
  }

  getTipoVariavelNome(item: any): string {
    // Se for um número (ID), buscar no dropdown
    if (typeof item === 'number') {
      const tipo = this.dropDownTipoVariavel.find(t => t.value === item);
      return tipo?.label || 'N/A';
    }
    
    // Se for um objeto com tipo_variavel
    if (item.tipo_variavel && typeof item.tipo_variavel === 'object') {
      return item.tipo_variavel.tipo_variavel || 'N/A';
    }
    
    // Se for um objeto com tipo_variavel_id
    if (item.tipo_variavel_id) {
      const tipo = this.dropDownTipoVariavel.find(t => t.value === item.tipo_variavel_id);
      return tipo?.label || 'N/A';
    }
    
    return item.tipo_variavel || 'N/A';
  }

  formatarDataHora(dataHora: string): string {
    // Converte data/hora como no dashboard: remove 'Z' se existir e aplica utcOffset(-180)
    const dataHoraStr = dataHora.replace('Z', '');
    const dataHoraBrasilia = moment(dataHoraStr).utcOffset(-180, true);
    return dataHoraBrasilia.format('DD/MM/YYYY HH:mm:ss');
  }

  formatarData(data: Date | null): string {
    if (!data) return '';
    return moment(data).format('DD/MM/YYYY');
  }

  forcarIntegracao(): void {
    if (!this.estacaoSelecionada || !this.tipoVariavelSelecionado || !this.dataInicial || !this.dataFinal) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'É necessário filtrar os dados antes de forçar a integração' 
      });
      return;
    }

    this.confirmationService.confirm({
      key: 'globalConfirm',
      message: `Deseja realmente forçar a integração dos dados filtrados?\n\nEstação: ${this.dropDownEstacoes.find(e => e.value === this.estacaoSelecionada)?.label}\nTipo Variável: ${this.dropDownTipoVariavel.find(t => t.value === this.tipoVariavelSelecionado)?.label}\nPeríodo: ${this.formatarData(this.dataInicial)} a ${this.formatarData(this.dataFinal)}`,
      header: 'Confirmar Forçar Integração',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.loadingIntegracao = true;
        
        // Converte datas para horário de Brasília (UTC-3) como no dashboard
        const dataInicialBrasilia = moment(this.dataInicial).utcOffset(-180);
        const dataFinalBrasilia = moment(this.dataFinal).utcOffset(-180);
        
        const filtros: any = {
          estacao_id: this.estacaoSelecionada,
          data_inicial: dataInicialBrasilia.format('YYYY-MM-DD'),
          data_final: dataFinalBrasilia.format('YYYY-MM-DD')
        };
        
        // Só adiciona tipo_variavel_id se houver seleção
        if (this.tipoVariavelSelecionado) {
          filtros.tipo_variavel_id = this.tipoVariavelSelecionado;
        }

        this.dadosVariavelService.forcarIntegracao(filtros).subscribe({
          next: (res) => {
            this.messageService.add({ 
              severity: 'success', 
              summary: 'Sucesso', 
              detail: 'Integração forçada com sucesso' 
            });
            this.loadingIntegracao = false;
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'Erro ao forçar integração';
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Erro', 
              detail: errorMsg 
            });
            this.loadingIntegracao = false;
          }
        });
      }
    });
  }

  reprocessarOnixsat(): void {
    if (!this.estacaoSelecionada) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Selecione uma estação' 
      });
      return;
    }

    if (!this.dataInicial) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Selecione a data inicial do filtro' 
      });
      return;
    }

    this.confirmationService.confirm({
      key: 'globalConfirm',
      message: `Deseja realmente reprocessar os dados da estação?\n\nEstação: ${this.dropDownEstacoes.find(e => e.value === this.estacaoSelecionada)?.label}\nData Inicial: ${this.formatarData(this.dataInicial)}`,
      header: 'Confirmar Reprocessamento',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.loadingReprocessamento = true;
        // Converte data/hora para horário de Brasília (UTC-3) como no dashboard
        const dataHoraBrasilia = moment(this.dataInicial).utcOffset(-180);
        const dataHoraFormatada = dataHoraBrasilia.format('YYYY-MM-DD HH:mm:ss');

        this.onixsatService.buscarPorData(dataHoraFormatada, this.estacaoSelecionada).subscribe({
          next: (resultado) => {
            this.loadingReprocessamento = false;
            const msg = resultado?.mensagem || resultado?.message
              || 'Reprocessamento enfileirado. O processamento continua em background.';
            if (resultado?.queued || resultado?.sucesso || resultado?.success) {
              this.messageService.add({
                severity: 'success',
                summary: resultado?.queued ? 'Enfileirado' : 'Sucesso',
                detail: msg
              });
              this.filtrar();
            } else {
              this.messageService.add({
                severity: 'info',
                summary: 'Informação',
                detail: msg
              });
            }
          },
          error: (err) => {
            this.loadingReprocessamento = false;
            const errorMsg = err?.error?.message || 'Erro ao enfileirar reprocessamento';
            this.messageService.add({
              severity: err?.status === 409 ? 'warn' : 'error',
              summary: err?.status === 409 ? 'Em andamento' : 'Erro',
              detail: errorMsg
            });
          }
        });
      }
    });
  }

  closeModal(): void {
    this.displayModal = false;
    this.form.reset();
    // Restaurar validadores ao fechar
    this.form.get('tipo_variavel_id')?.setValidators([Validators.required]);
    this.form.get('tipo_variavel_id')?.updateValueAndValidity();
    this.form.get('data_hora_dado')?.setValidators([Validators.required]);
    this.form.get('data_hora_dado')?.updateValueAndValidity();
  }

  // ========== Métodos do Wizard de Importação em Lote ==========
  
  abrirWizardImportacao(): void {
    this.mostrarWizardImportacao = true;
    this.passoWizard = 1;
    this.resetarWizard();
  }

  resetarWizard(): void {
    this.arquivoSelecionado = null;
    this.estacaoWizard = null;
    this.dadosProcessados = [];
    this.previewData = [];
    this.previewLinhas = [];
    this.somenteNovos = false;
    this.previewVariaveis = [];
    this.colunasArquivo = [];
    this.colunasValor = [];
    this.colunaDataHora = null;
    this.excelUsa1904 = false;
    this.mapeamentoColunas = {};
    this.dadosExistentes.clear();
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  onFileSelect(event: any): void {
    const file = event.currentFiles?.[0];
    if (file) {
      const extensao = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(extensao || '')) {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: 'Formato de arquivo não suportado. Use CSV, XLSX ou XLS.' 
        });
        return;
      }
      this.arquivoSelecionado = file;
      this.processarArquivo(file);
    }
  }

  async processarArquivo(file: File): Promise<void> {
    this.processandoArquivo = true;
    try {
      const extensao = file.name.split('.').pop()?.toLowerCase();
      
      if (extensao === 'csv') {
        this.excelUsa1904 = false;
        await this.processarCSV(file);
      } else if (extensao === 'xlsx' || extensao === 'xls') {
        await this.processarExcel(file);
      }
      
      this.identificarColunas();
    } catch (error) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Erro', 
        detail: 'Erro ao processar arquivo: ' + (error as Error).message 
      });
    } finally {
      this.processandoArquivo = false;
    }
  }

  async processarCSV(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const text = e.target.result;
          const lines = text.split(/\r?\n/).filter((line: string) => line.trim());
          
          // Detectar delimitador
          const detectarDelimitador = (linha: string): string => {
            const virgulas = (linha.match(/,/g) || []).length;
            const pontoEVirgula = (linha.match(/;/g) || []).length;
            return pontoEVirgula > virgulas ? ';' : ',';
          };
          
          const delimitador = detectarDelimitador(lines[0]);
          const headers = lines[0].split(delimitador).map((h: string) => h.trim().replace(/"/g, ''));
          this.colunasArquivo = headers;
          
          this.dadosProcessados = lines.slice(1)
            .filter((line: string) => line.trim())
            .map((line: string, index: number) => {
              const values = line.split(delimitador).map((v: string) => v.trim().replace(/"/g, ''));
              const obj: any = {};
              headers.forEach((header, i) => {
                obj[header] = values[i] || '';
              });
              obj._linha = index + 2;
              return obj;
            });
          
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo CSV'));
      reader.readAsText(file, 'UTF-8');
    });
  }

  async processarExcel(file: File): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Importação dinâmica do XLSX
        const XLSX = await import('xlsx');
        const reader = new FileReader();
        
        reader.onload = (e: any) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            this.excelUsa1904 = !!workbook.Workbook?.WBProps?.date1904;
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            
            if (jsonData.length === 0) {
              reject(new Error('Arquivo Excel vazio'));
              return;
            }
            
            const headers = (jsonData[0] as any[]).map((h: any) => String(h || '').trim());
            this.colunasArquivo = headers;
            
            // Primeiro, obter dados com raw: true para preservar números seriais de datas
            const jsonDataRaw = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '',
              raw: true
            });
            
            // Também obter dados formatados para ter strings quando disponível
            const jsonDataFormatted = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '',
              raw: false
            });
            
            this.dadosProcessados = (jsonDataRaw.slice(1) as any[][])
              .filter((row: any[]) => row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== ''))
              .map((row: any[], index: number) => {
                const obj: any = {};
                const rowFormatted = (jsonDataFormatted.slice(1) as any[][])[index] || [];
                headers.forEach((header, i) => {
                  const valorRaw = row[i];
                  const valorFormatted = rowFormatted[i];
                  
                  if (valorRaw !== undefined && valorRaw !== null) {
                    // Preservar o tipo original: número (serial date) ou string formatada
                    if (typeof valorRaw === 'number') {
                      obj[header] = valorRaw;
                    } else if (valorFormatted !== undefined && valorFormatted !== null) {
                      obj[header] = String(valorFormatted).trim();
                    } else {
                      const valorStr = String(valorRaw).trim();
                      obj[header] = valorStr || '';
                    }
                  } else {
                    obj[header] = '';
                  }
                });
                obj._linha = index + 2;
                return obj;
              });
            
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => reject(new Error('Erro ao ler arquivo Excel'));
        reader.readAsArrayBuffer(file);
      } catch (error) {
        reject(error);
      }
    });
  }

  identificarColunas(): void {
    const normalizarNome = (nome: string): string => {
      return nome.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
    };
    
    const colunasDataHora: string[] = [];
    const colunasValor: string[] = [];
    
    this.colunasArquivo.forEach(col => {
      const colNormalizada = normalizarNome(col);
      if (colNormalizada.includes('data') || 
          colNormalizada.includes('hora') || 
          colNormalizada.includes('date') || 
          colNormalizada.includes('time') ||
          colNormalizada.includes('hour')) {
        colunasDataHora.push(col);
      } else {
        colunasValor.push(col);
      }
    });
    
    this.colunasValor = colunasValor;
    
    // Identificar coluna de data/hora (priorizar coluna combinada)
    this.colunaDataHora = colunasDataHora.find(col => {
      const colNormalizada = normalizarNome(col);
      return colNormalizada.includes('data/hora') || 
             colNormalizada.includes('datahora') ||
             (colNormalizada.includes('data') && colNormalizada.includes('hora'));
    }) || colunasDataHora[0] || null;
    
    // Limpar mapeamento anterior
    this.mapeamentoColunas = {};
    
    // Tentar identificar automaticamente tipos de variável pelo nome da coluna
    this.colunasValor.forEach(col => {
      const colNormalizada = normalizarNome(col);
      const tipoEncontrado = this.dropDownTipoVariavel.find(tipo => {
        const tipoNormalizado = normalizarNome(tipo.label);
        return colNormalizada.includes(tipoNormalizado) || 
               tipoNormalizado.includes(colNormalizada) ||
               (colNormalizada.includes('nivel') && tipoNormalizado.includes('nivel')) ||
               (colNormalizada.includes('bateria') && tipoNormalizado.includes('bateria'));
      });
      
      if (tipoEncontrado) {
        this.mapeamentoColunas[col] = tipoEncontrado.value;
      } else {
        this.mapeamentoColunas[col] = null;
      }
    });
  }

  getOpcoesTipoVariavel(colunaAtual: string) {
    const selecionadoAtual = this.mapeamentoColunas[colunaAtual];
    const selecionados = new Set(
      Object.entries(this.mapeamentoColunas)
        .filter(([col, tipo]) => col !== colunaAtual && !!tipo)
        .map(([, tipo]) => tipo as number)
    );

    return this.dropDownTipoVariavel.map(opt => ({
      ...opt,
      disabled: selecionados.has(opt.value) && opt.value !== selecionadoAtual
    }));
  }

  mapeamentoValido(): boolean {
    if (this.colunasValor.length === 0 || !this.colunaDataHora) return false;
    const possuiNaoMapeado = this.colunasValor.some(col => !this.mapeamentoColunas[col]);
    if (possuiNaoMapeado) return false;

    const selecionados = this.colunasValor
      .map(col => this.mapeamentoColunas[col])
      .filter((v): v is number => !!v);
    const temDuplicata = new Set(selecionados).size !== selecionados.length;
    return !temDuplicata;
  }

  irParaMapeamento(): void {
    if (this.arquivoSelecionado && this.estacaoWizard) {
      this.passoWizard = 2;
    }
  }

  async gerarPreview(): Promise<void> {
    if (!this.mapeamentoValido()) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Complete o mapeamento de todas as colunas' 
      });
      return;
    }

    this.loading = true;
    
    try {
      await this.buscarDadosExistentes();
      this.processarDadosParaPreview();
      this.passoWizard = 3;
    } catch (error: any) {
      const detail = error?.message === 'Medições duplicadas'
        ? 'Há medições duplicadas para a mesma variável e horário. Revise os dados existentes antes de importar.'
        : 'Não foi possível verificar os dados existentes. Tente novamente.';
      this.messageService.add({ severity: 'error', summary: 'Erro', detail });
    } finally {
      this.loading = false;
    }
  }

  async buscarDadosExistentes(): Promise<void> {
    if (!this.estacaoWizard) throw new Error('Estação não selecionada');
    const tipos = Object.values(this.mapeamentoColunas).filter((v): v is number => !!v);
    const datas = this.dadosProcessados.map(row => this.extrairDataHora(row)).filter((d): d is string => !!d).sort();
    if (!datas.length || !tipos.length) throw new Error('Nenhum dado válido');
    this.dadosExistentes.clear();
    let page = 1;
    let lastPage = 1;
    do {
      const res: any = await firstValueFrom(this.dadosVariavelService.listar({
        estacao_id: this.estacaoWizard, tipo_variavel_id: tipos,
        data_inicial: datas[0], data_final: datas[datas.length - 1], per_page: 1000, page, resumo_importacao: true
      }));
      if (!Array.isArray(res.data) || !res.last_page) throw new Error('Resposta inválida');
      for (const dado of res.data) {
        const key = `${dado.estacao_id}|${dado.tipo_variavel_id}|${moment.parseZone(dado.data_hora_dado).format('YYYY-MM-DD HH:mm:ss')}`;
        const existentes = this.dadosExistentes.get(key) || [];
        existentes.push({ id: dado.id, valor: dado.valor });
        this.dadosExistentes.set(key, existentes);
      }
      lastPage = res.last_page;
      page++;
    } while (page <= lastPage);
  }

  extrairDataHora(row: any): string | null {
    if (!this.colunaDataHora || !row[this.colunaDataHora]) return null;

    const valor = row[this.colunaDataHora];
    
    // Se for um objeto Date, converter diretamente
    if (valor instanceof Date) {
      const data = moment(valor);
      if (data.isValid()) {
        return data.format('YYYY-MM-DD HH:mm:ss');
      }
      return null;
    }

    // Datas do Excel representam horário de calendário, sem fuso horário.
    if (typeof valor === 'number') {
      if (valor > 1 && valor < 1000000) {
        const epoch = this.excelUsa1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
        const milissegundos = epoch + Math.round(valor * 86400) * 1000;
        const data = moment.utc(milissegundos);
        if (data.isValid() && data.year() >= 1900 && data.year() <= 2100) {
          return data.format('YYYY-MM-DD HH:mm:ss');
        }
      }
      return null;
    }

    const valorStr = String(valor).trim();
    if (!valorStr) return null;
    
    // Tentar parse de data brasileira (DD/MM/YYYY HH:mm:ss ou DD/MM/YYYY)
    const parseDataBrasileira = (str: string): moment.Moment | null => {
      // Formato: DD/MM/YYYY HH:mm:ss
      let match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
      if (match) {
        const data = moment(`${match[3]}-${match[2]}-${match[1]} ${match[4]}:${match[5]}:${match[6]}`, 'YYYY-MM-DD HH:mm:ss', true);
        if (data.isValid() && data.year() >= 1900 && data.year() <= 2100) {
          return data;
        }
      }
      
      // Formato: DD/MM/YYYY
      match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        const data = moment(`${match[3]}-${match[2]}-${match[1]}`, 'YYYY-MM-DD', true);
        if (data.isValid() && data.year() >= 1900 && data.year() <= 2100) {
          return data;
        }
      }
      
      return null;
    };

    // Tentar parse brasileiro primeiro
    const dataBrasileira = parseDataBrasileira(valorStr);
    if (dataBrasileira && dataBrasileira.isValid()) {
      return dataBrasileira.format('YYYY-MM-DD HH:mm:ss');
    }

    // Tentar parse ISO com validação estrita
    const dataISO = moment(valorStr, moment.ISO_8601, true);
    if (dataISO.isValid() && dataISO.year() >= 1900 && dataISO.year() <= 2100) {
      return dataISO.format('YYYY-MM-DD HH:mm:ss');
    }

    // Última tentativa: parse genérico (menos confiável)
    const dataGenerica = moment(valorStr);
    if (dataGenerica.isValid() && dataGenerica.year() >= 1900 && dataGenerica.year() <= 2100) {
      return dataGenerica.format('YYYY-MM-DD HH:mm:ss');
    }

    return null;
  }

  processarDadosParaPreview(): void {
    this.previewData = [];
    
    this.dadosProcessados.forEach((linha, index) => {
      const dataHoraStr = this.extrairDataHora(linha);
      if (!dataHoraStr) return;

      const dataHora = moment(dataHoraStr);
      if (!dataHora.isValid()) return;

      this.colunasValor.forEach(col => {
        const tipoVariavelId = this.mapeamentoColunas[col];
        if (!tipoVariavelId) return;

        const valor = linha[col];
        if (valor === null || valor === undefined || String(valor).trim() === '') return;

        let valorStr = String(valor).trim();
        valorStr = valorStr.replace(',', '.');
        const valorNum = parseFloat(valorStr);
        
        if (!isNaN(valorNum)) {
          const dataHoraFormatada = dataHora.format('YYYY-MM-DD HH:mm:ss');
          const keyExistente = `${this.estacaoWizard}|${tipoVariavelId}|${dataHoraFormatada}`;
          const dadoExistente = this.dadosExistentes.get(keyExistente);
          
          this.previewData.push({
            estacao_id: this.estacaoWizard,
            tipo_variavel_id: tipoVariavelId,
            data_hora_dado: dataHoraFormatada,
            valor: valorNum,
            _linha: linha._linha,
            _coluna: col,
            _existente: !!dadoExistente?.length,
            _dados_existentes: dadoExistente || []
          });
        }
      });
    });
    
    // Ordenar por data/hora
    this.previewData.sort((a, b) => {
      return moment(a.data_hora_dado).diff(moment(b.data_hora_dado));
    });
    this.atualizarPreview();
  }

  atualizarPreview(): void {
    this.previewVariaveis = this.colunasValor.map(col => this.mapeamentoColunas[col]).filter((id): id is number => id != null);
    const grupos = new Map<string, any[]>();
    for (const item of this.dadosSelecionados()) {
      let linhas = grupos.get(item.data_hora_dado);
      if (!linhas) { linhas = []; grupos.set(item.data_hora_dado, linhas); }
      let linha = linhas.find(l => !l.valores[item.tipo_variavel_id]);
      if (!linha) { linha = { data_hora_dado: item.data_hora_dado, valores: {} }; linhas.push(linha); }
      const atual = this.dadosExistentes.get(`${this.estacaoWizard}|${item.tipo_variavel_id}|${item.data_hora_dado}`);
      linha.valores[item.tipo_variavel_id] = { importado: item.valor, atual: atual?.map((d: any) => `${d.valor} (ID ${d.id})`).join(', '),
        classe: !atual?.length ? '' : atual.every((d: any) => Number(d.valor) === item.valor) ? 'preview-igual' : 'preview-diferente' };
    }
    this.previewLinhas = Array.from(grupos.values()).flat();
    for (const linha of this.previewLinhas) {
      for (const tipo of this.previewVariaveis) {
        if (!linha.valores[tipo]) {
          const atual = this.dadosExistentes.get(`${this.estacaoWizard}|${tipo}|${linha.data_hora_dado}`);
          if (atual?.length) linha.valores[tipo] = { atual: atual.map((d: any) => `${d.valor} (ID ${d.id})`).join(', '), classe: '' };
        }
      }
    }
  }

  dadosSelecionados(): any[] {
    return this.somenteNovos ? this.previewData.filter(d => !d._existente) : this.previewData;
  }

  importarEmLote(): void {
    if (this.dadosSelecionados().length === 0) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Nenhum dado para importar' 
      });
      return;
    }

    // Verificar se há dados existentes
    const dadosExistentes = this.dadosSelecionados().filter(d => d._existente);
    const totalExistentes = dadosExistentes.length;
    const totalRegistrosExistentes = dadosExistentes.reduce((total, d) => total + d._dados_existentes.length, 0);

    if (totalExistentes > 0) {
      this.confirmationService.confirm({
        key: 'globalConfirm',
        message: `${totalExistentes} medição(ões) do arquivo correspondem a ${totalRegistrosExistentes} registro(s) existentes. Todos os registros correspondentes, inclusive duplicados, terão seus valores substituídos. Deseja continuar?`,
        header: 'Confirmar Substituição',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, substituir',
        rejectLabel: 'Cancelar',
        accept: () => {
          this.executarImportacaoLote();
        }
      });
    } else {
      this.executarImportacaoLote();
    }
  }

  executarImportacaoLote(): void {
    if (!this.estacaoWizard) return;
    this.loading = true;
    const dados = this.dadosSelecionados().map(d => ({
      tipo_variavel_id: d.tipo_variavel_id,
      data_hora_dado: d.data_hora_dado,
      valor: d.valor,
      existentes: d._dados_existentes
    }));
    this.dadosVariavelService.importar(this.estacaoWizard, dados, this.somenteNovos).subscribe({
      next: res => {
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: `${res.criados} registro(s) criado(s) e ${res.atualizados} atualizado(s).` });
        this.loading = false;
        this.mostrarWizardImportacao = false;
        this.resetarWizard();
        this.filtrar();
      },
      error: err => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: err?.error?.message || 'Não foi possível concluir a importação. Confira o resultado antes de tentar novamente.' });
      }
    });
  }

  // Métodos auxiliares para o template
  getTotalRegistrosPreview(): number {
    return this.previewData.length;
  }

  getTotalRegistrosExistentes(): number {
    return this.previewData.filter(d => d._existente).length;
  }

  getPreviewDataLimitada(): any[] {
    return this.previewData.slice(0, 100);
  }

  voltarParaMapeamento(): void {
    this.passoWizard = 2;
  }

}

